import type { ConnectionResponse } from '~/features/onboarding/types/connection.types';

import { isMetaConnection, isMetaConnectorCode } from './meta-connector';

/**
 * Connector codes that identify a Google Merchant Center destination. Runs carry
 * the backend's short code (`GMC`) or the connector type (`GOOGLE_MERCHANT`) —
 * both mean the same destination.
 */
const GMC_CONNECTOR_CODES = new Set(['GMC', 'GOOGLE_MERCHANT']);

/** `true` when a run's / job's connector code is a Google Merchant Center destination. */
export function isGmcConnectorCode(code: string | null | undefined): boolean {
  return code != null && GMC_CONNECTOR_CODES.has(code.toUpperCase());
}

/** `true` when a connection is a Google Merchant Center connection. */
export function isGmcConnection(connection: ConnectionResponse | undefined): boolean {
  return connection?.type === 'GOOGLE_MERCHANT';
}

/**
 * Destinations the connection workspace serves: Meta Commerce, and Google
 * Merchant Center since backend PR #703 brought GMC per-product delivery
 * tracking to Meta parity (ingest status, per-product feed rows, offer-id
 * timeline, blocked causes). Used to decide where "open connection" and
 * per-product History links appear.
 */
export function isWorkspaceConnectorCode(code: string | null | undefined): boolean {
  return isMetaConnectorCode(code) || isGmcConnectorCode(code);
}

/** `true` when the connection workspace can serve this connection. */
export function isWorkspaceConnection(connection: ConnectionResponse | undefined): boolean {
  return isMetaConnection(connection) || isGmcConnection(connection);
}

/** Delivery methods that ship a whole file — a per-product re-push (`product_ids`) is rejected for them. */
const FILE_DELIVERY_METHODS = new Set(['SFTP', 'FEEDONOMICS', 'FILE', 'SUPPLEMENTAL_FEED']);

/** The connection's delivery method from its config (`""` counts as unset). */
export function connectionDeliveryMethod(connection: ConnectionResponse): string | null {
  const raw = connection.config.delivery_method ?? connection.config.deliveryMethod;
  return typeof raw === 'string' && raw.trim() !== '' ? raw.toUpperCase() : null;
}

/**
 * Whether blocked products can be re-pushed one by one. SFTP connections and
 * GMC connections delivering by file ship the whole feed, so the backend
 * rejects `product_ids` (422); API-delivering GMC and Meta's item-batch path can.
 */
export function canRepushProducts(connection: ConnectionResponse): boolean {
  if (connection.type === 'SFTP') return false;
  if (isGmcConnection(connection)) {
    const method = connectionDeliveryMethod(connection);
    return method === null || !FILE_DELIVERY_METHODS.has(method);
  }
  return true;
}
